// @ts-ignore
import { saveAs } from 'file-saver';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  ConfirmationDialog
} from "projects/common/src/lib/modals/confirmation-dialog/confirmation-dialog.component";
import { showSnackbar } from "projects/common/src/lib/components/snackbar/snackbar.component";
import { ModalBehavior, ModalsService } from "projects/common/src/lib/services/modals.service";
import { EstimatesService, EstimateVersionRestore } from "../../services/estimates.service";
import { Router } from "@angular/router";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { debounceTime, filter, take } from "rxjs/operators";
import { RequestInvitationModal } from "../../modals/request-invitation/request-invitation.component";
import { UsersService } from "../../services/users.service";
import { PdfService } from "projects/common/src/lib/services/pdf.service";
import { Estimate, EstimateVersion } from "../../models/estimate.model";
import { BusinessService } from "../../services/business.service";
import { InvoiceCreate, InvoicesService } from "../../services/invoices.service";
import { BehaviorSubject } from "rxjs";
import { clientFromEstimateVersion } from "projects/common/src/lib/models/client-transform";
import { WorkflowService } from "../../services/workflow.service";
import { ScheduleService } from "../../services/schedule.service";
import { MultiformErrors, RequestInvitationFormData } from "../../modals/request-invitation/request-invitation.model";

@Component({
  selector: 'app-estimate-actions',
  templateUrl: './estimate-actions.component.html',
  styleUrls: ['./estimate-actions.component.scss']
})
export class EstimateActionsComponent implements OnChanges {

  @Input() estimate!: Estimate;
  @Input() currentEstimateVersion!: EstimateVersion;
  @Input() preview?: boolean = false;
  @Input() sendLoading?: boolean = false;
  @Input() depositFocused: boolean = false;
  @Input() disableActions: boolean = false;

  @Output() setCurrentEstimateVersionId = new EventEmitter<number>();
  @Output() previewEvent = new EventEmitter<boolean>();
  @Output() sendEvent = new EventEmitter();

  _depositFocused$ = new BehaviorSubject<boolean>(false);
  depositFocused$ = this._depositFocused$.asObservable().pipe(
    debounceTime(100)
  );

  openedActions = false;

  downloading = false;

  constructor(
    private modalsService: ModalsService,
    private estimatesService: EstimatesService,
    private businessService: BusinessService,
    private router: Router,
    private snackBar: MatSnackBar,
    private usersService: UsersService,
    private pdfService: PdfService,
    private invoicesService: InvoicesService,
    private workflowService: WorkflowService,
    private scheduleService: ScheduleService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if ('depositFocused' in changes)
      this._depositFocused$.next(this.depositFocused);
  }

  async deleteEstimate() {
    await this.modalsService.open(ConfirmationDialog, {
      behavior: ModalBehavior.Dialog,
      disableClose: true,
      data: {
        title: 'Delete Estimate',
        message: 'Are you sure you want to delete the estimate?',
        actionTitle: 'Delete',
        actionColor: 'warn',
        action: async () => {
          const currentVersion = this.currentEstimateVersion;
          const currentEstimate = this.estimate;
          const lastVersion = await this.estimatesService.deleteEstimateVersion(currentVersion.id);

          lastVersion
            ? await this.router.navigate(['/lobby/estimates/won'])
            : this.setCurrentEstimateVersionId.emit(this.estimatesService.findHighestVersionId(currentEstimate.versions, [currentVersion.id]));

          showSnackbar(this.snackBar, {
            message: 'Estimate deleted',
            duration: 10000,
            actionText: 'Undo',
            action: (async () => {
              const user = await this.usersService.currentUser$.pipe(take(1)).toPromise();
              const data: EstimateVersionRestore = {
                inClient: {
                  address: currentVersion.clientAddress,
                  businessName: currentVersion.clientBusinessName,
                  email: currentVersion.clientEmail,
                  extNumber: currentVersion.clientExtNumber,
                  firstName: currentVersion.clientFirstName,
                  lastName: currentVersion.clientLastName,
                  phoneNumber: currentVersion.clientPhoneNumber,
                  salesTaxPercentage: currentVersion.clientSalesTaxPercentage!,
                  type: currentVersion.clientType,
                  unit: currentVersion.clientUnit,
                },
                inCreatedBy: user.id,
                inWorkflowId: currentEstimate.workflowId,
                inId: currentEstimate.id,
                inVersionId: currentVersion.id,
                inVersionCreatedAt: currentVersion.createdAt,
                inSignature: currentVersion.signature,
                inDepositType: currentVersion.depositType,
                inDiscountType: currentVersion.discountType,
              }

              if (currentVersion.note)
                data.inNote = currentVersion.note;
              if (currentVersion.depositAmount)
                data.inDepositAmount = currentVersion.depositAmount;
              if (currentVersion.depositPercent)
                data.inDepositPercent = currentVersion.depositPercent;
              if (currentVersion.items)
                data.inItems = currentVersion.items;
              const id = await this.estimatesService.restoreEstimateVersion(data);
              !lastVersion
                ? this.setCurrentEstimateVersionId.emit(currentVersion.id)
                : await this.router.navigate([`/estimates/${currentEstimate.workflowId}/${id}`]);
            }).bind(this),
          });
        }
      }
    });
  }

  async markAsDeclined() {
    await this.modalsService.open(ConfirmationDialog, {
      behavior: ModalBehavior.Dialog,
      disableClose: true,
      data: {
        title: 'Mark as Declined',
        message: 'Are you sure you want to mark the estimate as declined?',
        actionTitle: 'Yes',
        actionColor: 'warn',
        action: async () => {
          await this.estimatesService.declineEstimate(this.currentEstimateVersion.id);

          showSnackbar(this.snackBar, {
            message: 'Marked as declined',
            duration: 2000,
          });
        }
      }
    });
  }

  async resendEstimateVersion() {
    await this.estimatesService.sendEstimatelEmail(this.currentEstimateVersion.clientEstimateId!, this.currentEstimateVersion.sentBy!);
    showSnackbar(this.snackBar, {
      message: 'Estimate resent',
      duration: 2000,
    });
  }

  async createOrGoToInvoice() {
    const currentUser = await this.usersService.currentUser$.pipe(take(1)).toPromise();
    if (!this.estimate.invoiceId) {
      const data: InvoiceCreate = {
        inCreatedBy: currentUser.id,
        inClient: {
          firstName: this.currentEstimateVersion.clientFirstName,
          lastName: this.currentEstimateVersion.clientLastName,
          phoneNumber: this.currentEstimateVersion.clientPhoneNumber,
          address: this.currentEstimateVersion.clientAddress,
          type: this.currentEstimateVersion.clientType,
          email: this.currentEstimateVersion.clientEmail,
          unit: this.currentEstimateVersion.clientUnit,
          extNumber: this.currentEstimateVersion.clientExtNumber,
          businessName: this.currentEstimateVersion.clientBusinessName
        },
        inWorkflowId: this.estimate.workflowId,
        inSignature: this.currentEstimateVersion.signature,
        inDiscountType: this.currentEstimateVersion.discountType,
      };

      if (this.currentEstimateVersion.note)
        data.inNote = this.currentEstimateVersion.note;

      if (this.currentEstimateVersion.items)
        data.inItems = this.currentEstimateVersion.items;

      const resData = await this.invoicesService.createInvoice(data);

      await this.router.navigate([`/invoices/${this.estimate.workflowId}/${resData.invoiceId}`]);
    } else {
      await this.router.navigate([`/invoices/${this.estimate.workflowId}/${this.estimate.invoiceId}`]);
    }
  }

  async createOrGoToJob() {
    if (!!this.estimate.jobId) {
      await this.router.navigate(['/jobs', this.estimate.workflowId, this.estimate.jobId]);
    } else if (!this.estimate.jobId && !!this.estimate.invoiceId) {
      return;
    } else if (!this.estimate.jobId && !this.estimate.invoiceId) {
      const workflow = await this.workflowService.workflowObservable(this.estimate.workflowId, 'pa').pipe(take(1)).toPromise();
      await this.modalsService.open(RequestInvitationModal, {
        behavior: ModalBehavior.Auto,
        breakpoint: RequestInvitationModal.BREAKPOINT,
        disableClose: true,
        data: {
          initialData: {
            id: this.estimate.id,
            workflowId: this.estimate.workflowId,
            dialogType: 'Estimate'
          },
          changes: {
            dialogType: 'Job',
            jobType: workflow?.jobType,
            client: clientFromEstimateVersion(this.currentEstimateVersion),
            assignees: [],
            ranges: [],
          },
          onSubmit: this.onJobCreateFromSubmit.bind(this)
        }
      });
    }
  }

  async onJobCreateFromSubmit(data: Required<RequestInvitationFormData>): Promise<MultiformErrors | null> {
    await this.scheduleService.createDocument(data, this.estimate.workflowId);
    const workflow = await this.workflowService.workflowObservable(this.estimate.workflowId).pipe(
      filter(workflow => workflow !== null && workflow.jobId !== null),
      take(1)
    ).toPromise();
    await this.router.navigate(['/jobs', this.estimate.workflowId, workflow!.jobId]);
    return null;
  }

  declineAllowed(): boolean {
    let allowed = true;

    if (
      this.currentEstimateVersion.status === 'canceled'
      || this.currentEstimateVersion.status === 'declined'
      || this.currentEstimateVersion.status === 'accepted'
      || this.estimate.invoiceId
      || this.estimate.jobId
    )
      allowed = false;

    if (this.estimate.sPaidAmount && this.estimate.sPaidAmount > 0)
      allowed = false;

    return allowed;
  }

  async downloadPdf() {
    if (!this.downloading) {
      this.downloading = true;
      const filename = `estimate-${this.estimate.uid}-${this.currentEstimateVersion.subUid}`;
      const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
      const pdfData = {
        filename,
        type: 'estimate',
        clientDomain: 'https://dev-client-slickestimates.web.app',
        businessId: business.businessId,
        recordId: this.currentEstimateVersion.clientEstimateId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const blob = await this.pdfService.generatePdf(pdfData).pipe(take(1)).toPromise();
      try {
        if (blob)
          saveAs(blob, filename);
      } catch (e) {
        console.log('Error during PDF download:', e);
        alert('PDF download failed!');
      } finally {
        this.downloading = false;
        showSnackbar(this.snackBar, {
          message: 'Estimate downloaded',
          duration: 2000,
        });
      }
    }
  }

  previewClick(depositFocused: boolean) {
    if (!depositFocused && this.currentEstimateVersion?.items?.length !== 0) {
      this.preview = true;
      this.previewEvent.emit(true);
    }
  }
}
